import { createClient } from '@supabase/supabase-js';

// Define Types
export type Json =
  | string
  | number
  | boolean
  | null
  | { [key: string]: Json | undefined }
  | Json[]

export interface Database {
  public: {
    Tables: {
      notification_recipients: {
        Row: {
          created_at: string
          id: string
          notification_id: string
          recipient_name: string
          recipient_type: string
        }
        Insert: {
          created_at?: string
          id?: string
          notification_id: string
          recipient_name: string
          recipient_type: string
        }
        Update: {
          created_at?: string
          id?: string
          notification_id?: string
          recipient_name?: string
          recipient_type?: string
        }
      }
      notifications: {
        Row: {
          archived: boolean
          created_at: string
          exact_order_number: string | null
          id: string
          message: string
          priority: string
          status: string
          type: string
          updated_at: string
          user_id: string
          user_name: string
          vo_number: string | null
          is_deleted: boolean
          deleted_at: string | null
        }
        Insert: {
          archived?: boolean
          created_at?: string
          exact_order_number?: string | null
          id?: string
          message: string
          priority: string
          status: string
          type: string
          updated_at?: string
          user_id: string
          user_name: string
          vo_number?: string | null
          is_deleted?: boolean
          deleted_at?: string | null
        }
        Update: {
          archived?: boolean
          created_at?: string
          exact_order_number?: string | null
          id?: string
          message?: string
          priority?: string
          status?: string
          type?: string
          updated_at?: string
          user_id?: string
          user_name?: string
          vo_number?: string | null
          is_deleted?: boolean
          deleted_at?: string | null
        }
      }
      profiles: {
        Row: {
          avatar_url: string | null
          full_name: string | null
          id: string
          updated_at: string | null
          username: string | null
          website: string | null
        }
        Insert: {
          avatar_url?: string | null
          full_name?: string | null
          id: string
          updated_at?: string | null
          username?: string | null
          website?: string | null
        }
        Update: {
          avatar_url?: string | null
          full_name?: string | null
          id?: string
          updated_at?: string | null
          username?: string | null
          website?: string | null
        }
      }
      medewerkers: {
        Row: {
          id: string
          naam: string
          functie: string | null
          afdeling: string | null
          email: string | null
          username: string
          password: string
          created_at: string | null
          updated_at: string | null
          active: boolean | null
        }
        Insert: {
          id?: string
          naam: string
          functie?: string | null
          afdeling?: string | null
          email?: string | null
          username: string
          password: string
          created_at?: string | null
          updated_at?: string | null
          active?: boolean | null
        }
        Update: {
          id?: string
          naam?: string
          functie?: string | null
          afdeling?: string | null
          email?: string | null
          username?: string
          password?: string
          created_at?: string | null
          updated_at?: string | null
          active?: boolean | null
        }
      }
      inpakkers: {
        Row: {
          id: string
          naam: string
          actief: boolean | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          naam: string
          actief?: boolean | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          naam?: string
          actief?: boolean | null
          created_at?: string
          updated_at?: string
        }
      }
      notification_edit_history: {
        Row: {
          id: string
          notification_id: string
          editor_id: string
          editor_name: string
          edit_date: string
          previous_message: string | null
          new_message: string | null
          field_name: string | null
          previous_value: string | null
          new_value: string | null
          change_type: string | null
        }
        Insert: {
          id?: string
          notification_id: string
          editor_id: string
          editor_name: string
          edit_date?: string
          previous_message?: string | null
          new_message?: string | null
          field_name?: string | null
          previous_value?: string | null
          new_value?: string | null
          change_type?: string | null
        }
        Update: {
          id?: string
          notification_id?: string
          editor_id?: string
          editor_name?: string
          edit_date?: string
          previous_message?: string | null
          new_message?: string | null
          field_name?: string | null
          previous_value?: string | null
          new_value?: string | null
          change_type?: string | null
        }
      }
      order_notes: {
        Row: {
          id: string
          reference_id: string
          reference_type: string
          text: string
          author_id: string
          author_name: string
          timestamp: string
        }
        Insert: {
          id?: string
          reference_id: string
          reference_type: string
          text: string
          author_id: string
          author_name: string
          timestamp?: string
        }
        Update: {
          id?: string
          reference_id?: string
          reference_type?: string
          text?: string
          author_id?: string
          author_name?: string
          timestamp?: string
        }
      }
    }
    Views: {
      [_ in never]: never
    }
    Functions: {
      [_ in never]: never
    }
    Enums: {
      [_ in never]: never
    }
    CompositeTypes: {
      [_ in never]: never
    }
  }
}

// Define convenient type aliases for Tables
export type Tables<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Row'];
export type TablesInsert<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Insert'];
export type TablesUpdate<T extends keyof Database['public']['Tables']> = Database['public']['Tables'][T]['Update'];

// Export specific types for all tables
export type NotificationsResponse = Tables<'notifications'>;
export type NotificationRecipientsResponse = Tables<'notification_recipients'>;
export type MedewerkerResponse = Tables<'medewerkers'> & {
  last_logout?: string | null;
  last_login?: string | null;
};
export type MedewerkerInsert = TablesInsert<'medewerkers'>;
export type MedewerkerUpdate = TablesUpdate<'medewerkers'>;
export type InpakkerResponse = Tables<'inpakkers'>;
export type NotificationEditHistoryResponse = Tables<'notification_edit_history'>;
export type OrderNoteResponse = Tables<'order_notes'>;
export type OrderNoteInsert = TablesInsert<'order_notes'>;
export type OrderNoteUpdate = TablesUpdate<'order_notes'>;

// Supabase client - NEW project configuration
const supabaseUrl = 'https://bpxebrbhhywoayvqwdnh.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJweGVicmJoaHl3b2F5dnF3ZG5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQ3OTQwNzUsImV4cCI6MjA2MDM3MDA3NX0.aMoMxNwfxI0MuSKxgYri4y1Xrf9tJtTflKAAyZzRRbE';

// Log initialization parameters for debugging
console.log('Initializing Supabase client with:', { 
  url: supabaseUrl, 
  keyLength: supabaseAnonKey ? supabaseAnonKey.length : 0 
});

// Custom fetch that injects the session token header for custom auth
const customFetch: typeof fetch = (input, init) => {
  const options = init || {};
  // Only run in browser environment
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    const token = localStorage.getItem('auth_session_token');
    const url = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
    
    // Debug logging for orders and auth_sessions requests
    const isOrdersRequest = url.includes('/orders_live_ls') || url.includes('/auth_sessions');
    if (isOrdersRequest) {
      const tokenPreview = token ? `${token.slice(0, 8)}...${token.slice(-4)}` : 'MISSING';
      console.log(`[customFetch] ${url.split('/rest/v1/')[1]?.split('?')[0] || 'unknown'} | token: ${tokenPreview}`);
    }
    
    if (token) {
      const headers = new Headers(options.headers);
      headers.set('x-session-token', token);
      return fetch(input, { ...options, headers });
    } else if (isOrdersRequest) {
      console.warn('[customFetch] No session token in localStorage - RLS will likely return empty results');
    }
  }
  return fetch(input, options);
};

// Supabase client with realtime enabled and custom fetch for session auth
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    global: {
      fetch: customFetch
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    }
  }
);

// Function to create and setup channel for notifications
export const setupNotificationsChannel = () => {
  console.log('Setting up dedicated channel for notifications');
  
  // Create a dedicated channel for notifications
  return supabase
    .channel('notifications-changes')
    .on('postgres_changes', 
      {
        event: '*',
        schema: 'public',
        table: 'notifications'
      }, 
      (payload) => {
        console.log('Notification change detected via dedicated channel:', payload);
      }
    )
    .subscribe((status) => {
      console.log('Notifications channel subscription status:', status);
    });
};

// Initialize the notifications channel
setupNotificationsChannel();
